import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, Link } from 'react-router-dom';
import { format, startOfWeek } from 'date-fns';
import { useAuth } from '../contexts/AuthContext';
import DashboardLayout from './DashboardLayout';
import AvailabilityMatrix from './availability/AvailabilityMatrix';
import InviteCodeManager from './member/InviteCodeManager';
import BookingRequests from './member/BookingRequests';
import PodBooking from './member/PodBooking';
import ProfilePhotoUpload from './member/ProfilePhotoUpload';
import Card, { CardHeader } from './ui/Card';
import Button from './ui/Button';
import StatusBanner from './ui/StatusBanner';
import MemberSettings from './member/MemberSettings';
import { Input, Textarea } from './ui/FormField';
import {
  ClockIcon,
  CalendarIcon,
  ChartBarIcon,
  CalendarDaysIcon,
  LinkIcon,
  ClipboardDocumentIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  PencilIcon,
  TicketIcon,
  InboxStackIcon,
  ArrowRightIcon,
  DocumentTextIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { availabilityAPI, memberProfileAPI, memberBillingAPI, ProfileUpdateRequest, BookingLinkResponse, UsageSummary, Invoice, UsageDetail } from '../services/api';
import UsageBreakdownModal from './billing/UsageBreakdownModal';
import { WeeklyAvailability } from '../types/availability';
import { BookingListItem } from '../types/booking';
import CancelBookingModal from './member/CancelBookingModal';
import { isInsideCancellationWindow, isWithinGrace, formatDeadline, formatGraceDeadline } from '../constants/policy';
import { usePolicy } from '../contexts/PolicyContext';
import { User } from '../types/auth';
import ChipInput from './ui/ChipInput';
import { COMMON_SPECIALTIES } from '../constants/specialties';
import ServicesEditor from './member/ServicesEditor';
import { formatDuration, getDurationMinutes } from '../utils/formatters';
import { formatBookingDateTime, formatEndTime, formatShortDate } from '../utils/timezone';
import toast from 'react-hot-toast';
import { apiErrorMessage } from '../utils/apiError';
import { saveDraft, loadDraft, clearDraft, draftDiffers } from '../utils/localDraft';

const MemberDashboard: React.FC = () => {
  return (
    <DashboardLayout>
      <Routes>
        <Route path="/" element={<MemberHome />} />
        <Route path="/availability" element={<AvailabilityMatrix />} />
        <Route path="/bookings" element={<MyBookings />} />
        <Route path="/invite-codes" element={<InviteCodeManager />} />
        <Route path="/booking-requests" element={<BookingRequests />} />
        <Route path="/book-pod" element={<PodBooking />} />
        <Route path="/billing" element={<Billing />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/settings" element={<MemberSettings />} />
      </Routes>
    </DashboardLayout>
  );
};

const MemberHome: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [weeklyAvailability, setWeeklyAvailability] = useState<WeeklyAvailability | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Get current week start (Monday)
        const weekStart = startOfWeek(new Date(), { weekStartsOn: 1 });
        const weekStartStr = format(weekStart, 'yyyy-MM-dd');

        const data = await availabilityAPI.getMyWeeklyAvailability(weekStartStr);
        setWeeklyAvailability(data);
      } catch (err) {
        setError('Failed to fetch dashboard data');
        console.error('Error fetching dashboard data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const handleSetAvailability = () => {
    navigate('/member/availability');
  };

  const handleViewBookings = () => {
    navigate('/member/bookings');
  };

  const handleManageInviteCodes = () => {
    navigate('/member/invite-codes');
  };

  const handleBookingRequests = () => {
    navigate('/member/booking-requests');
  };

  const getRecentActivity = () => {
    if (!weeklyAvailability) return [];

    const activities = [];

    // Add availability hours as activities (using API-calculated hours)
    if (weeklyAvailability.total_available_hours > 0) {
      activities.push({
        text: `${weeklyAvailability.total_available_hours} hours available this week`,
        type: 'availability'
      });
    }

    // Add booking activities (using API-calculated hours)
    if (weeklyAvailability.total_booked_hours > 0) {
      activities.push({
        text: `${weeklyAvailability.total_booked_hours} hours booked this week`,
        type: 'booking'
      });
    }

    return activities;
  };

  const activities = getRecentActivity();

  return (
    <div>
      <h1 className="text-3xl font-bold text-clinic-dark mb-8 font-body">Clinician Dashboard</h1>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <Card className="p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-clinic-gold/20 rounded-full flex items-center justify-center">
              <ClockIcon className="h-6 w-6 text-clinic-gold" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-clinic-muted">Hours Available</p>
              <p className="text-2xl font-semibold text-clinic-dark">
                {loading ? '--' : weeklyAvailability?.total_available_hours || '0'}
              </p>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-clinic-primary/20 rounded-full flex items-center justify-center">
              <CalendarIcon className="h-6 w-6 text-clinic-primary" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-clinic-muted">Hours Booked</p>
              <p className="text-2xl font-semibold text-clinic-dark">
                {loading ? '--' : weeklyAvailability?.total_booked_hours || '0'}
              </p>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-clinic-accent/20 rounded-full flex items-center justify-center">
              <ChartBarIcon className="h-6 w-6 text-clinic-accent" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-clinic-muted">Utilization</p>
              <p className="text-2xl font-semibold text-clinic-dark">
                {loading ? '--' : `${weeklyAvailability?.utilization_percentage || '0'}%`}
              </p>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-clinic-primary-lighter rounded-full flex items-center justify-center">
              <CalendarDaysIcon className="h-6 w-6 text-clinic-primary" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-clinic-muted">Available Hours</p>
              <p className="text-2xl font-semibold text-clinic-dark">
                {loading ? '--' : weeklyAvailability ?
                  (weeklyAvailability.total_available_hours - weeklyAvailability.total_booked_hours) : '0'}
              </p>
            </div>
          </div>
        </Card>
      </div>

      {error && (
        <div className="mb-6">
          <StatusBanner
            variant="error"
            title="Unable to load dashboard"
            message={error}
            dismissible
            onDismiss={() => setError(null)}
          />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6">
          <CardHeader title="Quick Actions" />
          <div className="space-y-3">
            <button
              onClick={handleSetAvailability}
              className="w-full p-4 text-left border border-clinic-primary-lighter rounded-lg hover:bg-clinic-cream/50 transition-colors group"
            >
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium text-clinic-dark">Set Availability</h3>
                  <p className="text-sm text-clinic-muted">Configure your available hours</p>
                </div>
                <ArrowRightIcon className="h-5 w-5 text-clinic-muted group-hover:text-clinic-primary transition-colors" />
              </div>
            </button>
            <button
              onClick={handleViewBookings}
              className="w-full p-4 text-left border border-clinic-primary-lighter rounded-lg hover:bg-clinic-cream/50 transition-colors group"
            >
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium text-clinic-dark">View Bookings</h3>
                  <p className="text-sm text-clinic-muted">Check your upcoming appointments</p>
                </div>
                <ArrowRightIcon className="h-5 w-5 text-clinic-muted group-hover:text-clinic-primary transition-colors" />
              </div>
            </button>
            {/* Only show invite codes for private clinicians (is_public explicitly false) */}
            {user && user.is_public === false && (
              <button
                onClick={handleManageInviteCodes}
                className="w-full p-4 text-left border border-clinic-primary-lighter rounded-lg hover:bg-clinic-cream/50 transition-colors group"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <TicketIcon className="h-5 w-5 text-clinic-gold mr-2" />
                    <div>
                      <h3 className="font-medium text-clinic-dark">Manage Invite Codes</h3>
                      <p className="text-sm text-clinic-muted">Generate codes for private client access</p>
                    </div>
                  </div>
                  <ArrowRightIcon className="h-5 w-5 text-clinic-muted group-hover:text-clinic-gold transition-colors" />
                </div>
              </button>
            )}
            {/* Only show booking requests for approval-mode clinicians */}
            {user && user.requires_booking_approval && (
              <button
                onClick={handleBookingRequests}
                className="w-full p-4 text-left border border-clinic-primary-lighter rounded-lg hover:bg-clinic-cream/50 transition-colors group"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <InboxStackIcon className="h-5 w-5 text-clinic-primary mr-2" />
                    <div>
                      <h3 className="font-medium text-clinic-dark">Review Booking Requests</h3>
                      <p className="text-sm text-clinic-muted">Approve or reject client appointment requests</p>
                    </div>
                  </div>
                  <ArrowRightIcon className="h-5 w-5 text-clinic-muted group-hover:text-clinic-primary transition-colors" />
                </div>
              </button>
            )}
          </div>
        </Card>

        <Card className="p-6">
          <CardHeader title="This Week's Activity" />
          <div className="space-y-3">
            {loading ? (
              <div className="flex items-center justify-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-clinic-primary"></div>
              </div>
            ) : activities.length > 0 ? (
              activities.map((activity, index) => (
                <div key={index} className="flex items-center justify-between py-3 border-b border-clinic-primary-lighter last:border-0">
                  <div className="flex items-center">
                    <div className={`w-2 h-2 rounded-full mr-3 ${activity.type === 'booking' ? 'bg-clinic-primary' : 'bg-clinic-gold'}`} />
                    <span className="text-sm text-clinic-dark">{activity.text}</span>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8">
                <CalendarIcon className="h-12 w-12 text-clinic-muted mx-auto mb-3" />
                <p className="text-clinic-muted">No activity this week</p>
                <p className="text-sm text-clinic-muted mt-1">Set your availability to start receiving bookings</p>
              </div>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};


const MyBookings: React.FC = () => {
  const { windowHours: CANCELLATION_WINDOW_HOURS, graceMinutes, selfCancelEnabled } = usePolicy();
  const [bookings, setBookings] = useState<BookingListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [extendingId, setExtendingId] = useState<number | null>(null);
  // Clinician cancellation — client appointments and the clinician's own room
  // bookings share one modal and one cancellation-window rule.
  const [cancelTarget, setCancelTarget] = useState<BookingListItem | null>(null);
  const [cancelSubmitting, setCancelSubmitting] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);

  const fetchBookings = async () => {
    try {
      setLoading(true);
      setError(null);
      const { memberBookingAPI } = await import('../services/api');
      const data = await memberBookingAPI.getMyBookings();
      setBookings(data);
    } catch (err: any) {
      setError(apiErrorMessage(err, 'Failed to fetch bookings'));
      console.error('Error fetching member bookings:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBookings();
  }, []);

  const handleExtendBuffer = async (bookingId: number) => {
    try {
      setExtendingId(bookingId);
      const { memberBookingAPI } = await import('../services/api');
      await memberBookingAPI.extendBuffer(bookingId);
      toast.success('15-minute buffer added');
      await fetchBookings();
    } catch (err: any) {
      toast.error(apiErrorMessage(err, 'Failed to add buffer'));
    } finally {
      setExtendingId(null);
    }
  };

  const handleConfirmClinicianCancel = async (payload: {
    reason: string; internal_note?: string; release_room: boolean;
  }) => {
    if (!cancelTarget) return;
    try {
      setCancelSubmitting(true);
      setCancelError(null);
      const { memberBookingAPI } = await import('../services/api');
      if (cancelTarget.is_self_booked) {
        const selfRes = await memberBookingAPI.cancelSelfBooking(cancelTarget.id, payload.reason || undefined);
        if (selfRes.released) {
          toast.success('Booking cancelled and room released');
        } else {
          toast(`Cancelled. It was within ${selfRes.window_hours} hours, so the room stays reserved and billed.`, { icon: '⚠️' });
        }
        setCancelTarget(null);
        await fetchBookings();
        return;
      }
      const res = await memberBookingAPI.cancelBooking(cancelTarget.id, payload);
      if (res.boundary_crossed) {
        toast(`Within ${res.window_hours} hours, so the room was kept and you will be billed.`, { icon: '⚠️' });
      } else if (res.released) {
        toast.success('Appointment cancelled and room released');
      } else {
        toast.success('Appointment cancelled — room kept (you will be billed)');
      }
      setCancelTarget(null);
      await fetchBookings();
    } catch (err: any) {
      setCancelError(apiErrorMessage(err, 'Failed to cancel the appointment.'));
    } finally {
      setCancelSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div>
        <h1 className="text-3xl font-bold text-clinic-dark mb-8 font-body">My Bookings</h1>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-clinic-primary"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <h1 className="text-3xl font-bold text-clinic-dark mb-8 font-body">My Bookings</h1>
        <StatusBanner
          variant="error"
          title="Unable to load bookings"
          message={error}
        />
      </div>
    );
  }

  const now = new Date();
  // Late-cancelled self-bookings (is_self_booked + cancelled) are still "upcoming" since the clinician holds the room
  const upcoming = bookings.filter(b =>
    new Date(b.start_time) > now && (b.status !== 'cancelled' || b.is_self_booked)
  );
  const past = bookings.filter(b =>
    new Date(b.start_time) <= now || (b.status === 'cancelled' && !b.is_self_booked)
  );

  const renderBookingCard = (booking: BookingListItem, isPast: boolean) => (
    <div key={booking.id} className={`border border-clinic-primary-lighter rounded-lg p-4 transition-colors ${isPast ? 'opacity-60' : 'hover:bg-clinic-cream/30'}`}>
      <div className="flex justify-between items-start">
        <div>
          <h3 className="font-medium text-clinic-dark">
            {booking.is_self_booked
              ? (booking.client_name || 'Self-Booked (Personal Use)')
              : (booking.patient_name || 'Unknown Client')}
          </h3>
          {booking.is_self_booked && booking.client_email && (
            <p className="text-sm text-clinic-muted">{booking.client_email}</p>
          )}
          {(() => {
            const { date, time } = formatBookingDateTime(booking.start_time);
            const baseDuration = getDurationMinutes(booking);
            const buffer = booking.buffer_minutes || 0;
            const totalDuration = baseDuration + buffer;
            const endTimeStr = formatEndTime(booking.start_time, totalDuration);
            return (
              <>
                <p className="text-sm text-clinic-muted">{date}</p>
                <p className="text-sm text-clinic-muted">{time} - {endTimeStr}</p>
              </>
            );
          })()}
          {booking.room_name && (
            <p className="text-sm text-clinic-muted">Room: {booking.room_name}</p>
          )}
          {booking.notes && (
            <p className="text-sm text-clinic-muted mt-2 italic">"{booking.notes}"</p>
          )}
        </div>
        <div className="text-right">
          <div className="flex flex-col items-end gap-1">
            {booking.is_self_booked && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-clinic-gold/20 text-clinic-brown">
                Self-Booked
              </span>
            )}
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
              booking.status === 'confirmed'
                ? 'bg-clinic-primary/20 text-clinic-primary'
                : booking.status === 'cancelled'
                ? 'bg-clinic-accent/20 text-clinic-accent'
                : 'bg-clinic-muted/20 text-clinic-muted'
            }`}>
              {booking.is_self_booked && booking.status === 'cancelled' ? 'room held' :
               isPast && booking.status === 'confirmed' ? 'completed' : booking.status}
            </span>
          </div>
          <p className="text-sm text-clinic-muted mt-1">
            {formatDuration(getDurationMinutes(booking))}
            {booking.buffer_minutes && booking.buffer_minutes > 0 ? ` (+${booking.buffer_minutes} min buffer)` : ''}
          </p>
          {/* Action buttons only for upcoming bookings.
              At most two show at once, so inline buttons read better than a kebab. */}
          {!isPast && booking.status !== 'cancelled' && (
            <p className="text-xs text-clinic-muted mt-2">
              {isWithinGrace(booking.created_at, new Date(), graceMinutes)
                ? `Made a mistake? Free to cancel until ${formatGraceDeadline(booking.created_at as string, graceMinutes)}`
                : isInsideCancellationWindow(booking.start_time, new Date(), booking.created_at, CANCELLATION_WINDOW_HOURS, graceMinutes)
                ? `Inside ${CANCELLATION_WINDOW_HOURS}h: cancelling keeps the room billed`
                : `Free cancellation until ${formatDeadline(booking.start_time, CANCELLATION_WINDOW_HOURS)}`}
            </p>
          )}
          {!isPast && (
            <div className="flex flex-wrap gap-2 justify-end mt-3">
              {selfCancelEnabled && booking.is_self_booked && booking.status !== 'cancelled' && (
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => { setCancelError(null); setCancelTarget(booking); }}
                >
                  Cancel booking
                </Button>
              )}
              {booking.status === 'confirmed' && !booking.buffer_minutes && !booking.is_self_booked && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => handleExtendBuffer(booking.id)}
                  isLoading={extendingId === booking.id}
                  disabled={extendingId === booking.id}
                >
                  + 15 min buffer
                </Button>
              )}
              {booking.status === 'confirmed' && !booking.is_self_booked && (
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => { setCancelError(null); setCancelTarget(booking); }}
                >
                  Cancel appointment
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div>
      <h1 className="text-3xl font-bold text-clinic-dark mb-8 font-body">My Bookings</h1>

      {/* Upcoming */}
      <Card className="p-6 mb-6">
        <CardHeader title="Upcoming" />
        {upcoming.length === 0 ? (
          <div className="text-center py-6">
            <CalendarIcon className="h-10 w-10 text-clinic-muted mx-auto mb-2" />
            <p className="text-clinic-muted text-sm">No upcoming appointments</p>
          </div>
        ) : (
          <div className="space-y-3">
            {upcoming.map(b => renderBookingCard(b, false))}
          </div>
        )}
      </Card>

      {/* Past */}
      {past.length > 0 && (
        <Card className="p-6">
          <CardHeader title="Past" />
          <div className="space-y-3">
            {past.map(b => renderBookingCard(b, true))}
          </div>
        </Card>
      )}

      <CancelBookingModal
        isOpen={cancelTarget !== null}
        onClose={() => { setCancelTarget(null); setCancelError(null); }}
        booking={cancelTarget}
        isWithinWindow={cancelTarget ? isInsideCancellationWindow(cancelTarget.start_time, new Date(), cancelTarget.created_at, CANCELLATION_WINDOW_HOURS, graceMinutes) : false}
        onConfirm={handleConfirmClinicianCancel}
        isSubmitting={cancelSubmitting}
        serverError={cancelError}
      />
    </div>
  );
};

const Billing: React.FC = () => {
  const [currentUsage, setCurrentUsage] = useState<UsageSummary | null>(null);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [detailPeriod, setDetailPeriod] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [usageOpen, setUsageOpen] = useState(false);
  const [usageDetail, setUsageDetail] = useState<UsageDetail | null>(null);
  const [usageLoading, setUsageLoading] = useState(false);
  const [usageError, setUsageError] = useState<string | null>(null);

  const openUsageDetail = async (period: string) => {
    setUsageOpen(true);
    setUsageDetail(null);
    setUsageError(null);
    setUsageLoading(true);
    try {
      setUsageDetail(await memberBillingAPI.getUsageDetail(period));
    } catch (err: any) {
      setUsageError(apiErrorMessage(err, 'Failed to load usage detail'));
    } finally {
      setUsageLoading(false);
    }
  };

  useEffect(() => {
    const fetchBillingData = async () => {
      try {
        setLoading(true);
        setError(null);
        // Fetch independently so one failure doesn't block the other
        try {
          const usage = await memberBillingAPI.getCurrentUsage();
          setCurrentUsage(usage);
        } catch (e) {
          console.warn('Could not load usage data:', e);
        }
        try {
          const invoiceList = await memberBillingAPI.getInvoices(12);
          const items = Array.isArray(invoiceList) ? invoiceList : (invoiceList.items || []);
          setInvoices(items as Invoice[]);
        } catch (e) {
          console.warn('Could not load invoices:', e);
        }
      } catch (err: any) {
        console.error('Error fetching billing data:', err);
        setError(apiErrorMessage(err, 'Failed to load billing information'));
      } finally {
        setLoading(false);
      }
    };

    fetchBillingData();
  }, []);

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      pending: 'bg-clinic-muted/20 text-clinic-muted',
      open: 'bg-clinic-primary/20 text-clinic-primary',
      paid: 'bg-clinic-primary-lighter text-clinic-primary-dark',
      failed: 'bg-clinic-accent/20 text-clinic-accent',
      void: 'bg-clinic-surface-sunken text-clinic-muted',
    };
    const icons: Record<string, React.ReactNode> = {
      pending: <ClockIcon className="h-4 w-4 mr-1" />,
      open: <ArrowPathIcon className="h-4 w-4 mr-1" />,
      paid: <CheckCircleIcon className="h-4 w-4 mr-1" />,
      failed: <XCircleIcon className="h-4 w-4 mr-1" />,
      void: <XCircleIcon className="h-4 w-4 mr-1" />,
    };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[status] || styles.pending}`}>
        {icons[status]}
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  if (loading) {
    return (
      <div>
        <h1 className="text-3xl font-bold text-clinic-dark mb-8 font-body">Billing</h1>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-clinic-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-3xl font-bold text-clinic-dark mb-6 font-body">Billing</h1>

      {error && (
        <div className="mb-6">
          <StatusBanner
            variant="error"
            title="Error"
            message={error}
            dismissible
            onDismiss={() => setError(null)}
          />
        </div>
      )}

      {/* Billing Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <Card className="p-4">
          <div className="flex items-center">
            <div className="w-10 h-10 bg-clinic-gold/20 rounded-full flex items-center justify-center">
              <ClockIcon className="h-5 w-5 text-clinic-gold" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-clinic-muted">This Month</p>
              <p className="text-lg font-semibold text-clinic-dark">
                {currentUsage?.total_hours.toFixed(2) || '0.00'} hours
              </p>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center">
            <div className="w-10 h-10 bg-clinic-primary/20 rounded-full flex items-center justify-center">
              <DocumentTextIcon className="h-5 w-5 text-clinic-primary" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-clinic-muted">Total Invoices</p>
              <p className="text-lg font-semibold text-clinic-dark">{invoices.length}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Current Period Usage */}
      {currentUsage && (() => {
        const categories = [
          { label: 'Peak Hours', value: currentUsage.peak_hours, color: 'text-clinic-accent', subtitle: null },
          { label: 'Standard Hours', value: currentUsage.standard_hours, color: 'text-clinic-gold', subtitle: null },
          { label: 'Off-Peak Hours', value: currentUsage.off_peak_hours, color: 'text-clinic-primary', subtitle: null },
          { label: 'Pod Usage', value: currentUsage.pod_hours || 0, color: 'text-clinic-brown', subtitle: currentUsage.pod_amount ? `$${currentUsage.pod_amount.toFixed(2)}` : null },
        ].filter(c => c.value > 0);

        return (
          <Card className="p-6 mb-6">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <h3 className="text-lg font-semibold text-clinic-dark">
                Current Period Usage ({currentUsage.billing_period})
              </h3>
              <div className="flex items-center gap-2">
                <input
                  type="month"
                  value={detailPeriod}
                  onChange={(e) => setDetailPeriod(e.target.value)}
                  aria-label="Month to view usage detail for"
                  className="px-2 py-1.5 text-sm border border-clinic-primary-lighter rounded-lg focus:outline-none focus:ring-2 focus:ring-clinic-primary focus:border-transparent text-clinic-dark"
                />
                <button
                  type="button"
                  onClick={() => detailPeriod && openUsageDetail(detailPeriod)}
                  disabled={!detailPeriod}
                  className="text-clinic-primary hover:text-clinic-primary-dark text-sm font-medium disabled:opacity-50"
                >
                  View details
                </button>
              </div>
            </div>
            {categories.length === 0 && (
              <p className="text-sm text-clinic-muted mb-4">No billed time yet this month.</p>
            )}
            <div className={`grid grid-cols-1 md:grid-cols-${Math.min(categories.length + 1, 5)} gap-4`}>
              {categories.map(cat => (
                <div key={cat.label} className="p-4 bg-clinic-cream/50 rounded-lg">
                  <p className="text-sm text-clinic-muted">{cat.label}</p>
                  <p className={`text-2xl font-semibold ${cat.color}`}>{cat.value.toFixed(2)}h</p>
                  {cat.subtitle && <p className="text-xs text-clinic-muted">{cat.subtitle}</p>}
                </div>
              ))}
              <div className="p-4 bg-clinic-primary/10 rounded-lg">
                <p className="text-sm text-clinic-muted">Total Hours</p>
                <p className="text-2xl font-semibold text-clinic-dark">{currentUsage.total_hours.toFixed(2)}h</p>
              </div>
            </div>
          </Card>
        );
      })()}

      {/* Invoice History */}
      <Card padding="none" className="overflow-hidden">
        <div className="px-6 py-4 border-b border-clinic-primary-lighter">
          <h3 className="text-lg font-semibold text-clinic-dark">Invoice History</h3>
        </div>
        {invoices.length === 0 ? (
          <div className="p-8 text-center">
            <DocumentTextIcon className="mx-auto h-12 w-12 text-clinic-muted mb-4" />
            <h3 className="text-lg font-medium text-clinic-dark mb-2">No invoices yet</h3>
            <p className="text-clinic-muted">Your invoices will appear here once generated.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-clinic-primary-lighter">
              <thead className="bg-clinic-cream/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-clinic-muted uppercase tracking-wider">Period</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-clinic-muted uppercase tracking-wider">Hours</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-clinic-muted uppercase tracking-wider">Credit</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-clinic-muted uppercase tracking-wider">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-clinic-muted uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-clinic-muted uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-clinic-primary-lighter bg-white">
                {invoices.map((invoice) => (
                  <tr key={invoice.id} className="hover:bg-clinic-cream/30 transition-colors">
                    <td className="px-6 py-4 font-medium text-clinic-dark">{invoice.billing_period}</td>
                    <td className="px-6 py-4 text-clinic-dark">{invoice.total_hours.toFixed(2)}h</td>
                    <td className="px-6 py-4 text-clinic-accent">{(invoice.credit_amount || 0) > 0 ? `-$${invoice.credit_amount.toFixed(2)}` : '—'}</td>
                    <td className="px-6 py-4 font-semibold text-clinic-dark">${invoice.total_amount.toFixed(2)}</td>
                    <td className="px-6 py-4">{getStatusBadge(invoice.status)}</td>
                    <td className="px-6 py-4">
                      <button
                        type="button"
                        onClick={() => openUsageDetail(invoice.billing_period)}
                        className="text-clinic-primary hover:text-clinic-primary-dark text-sm font-medium mr-4"
                      >
                        Details
                      </button>
                      <button
                        onClick={async () => {
                          try {
                            const { memberBillingAPI, triggerBlobDownload } = await import('../services/api');
                            const blob = await memberBillingAPI.downloadInvoicePdf(invoice.id);
                            triggerBlobDownload(blob, `INV-${invoice.billing_period}-${String(invoice.id).padStart(4, '0')}.pdf`);
                          } catch (e) {
                            toast.error('Failed to download invoice');
                          }
                        }}
                        className="text-clinic-primary hover:text-clinic-primary-dark text-sm font-medium"
                      >
                        Download PDF
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <UsageBreakdownModal
        isOpen={usageOpen}
        onClose={() => setUsageOpen(false)}
        detail={usageDetail}
        loading={usageLoading}
        error={usageError}
      />
    </div>
  );
};

const Profile: React.FC = () => {
  const { updateUser } = useAuth();
  const [profile, setProfile] = useState<User | null>(null);
  const [bookingLink, setBookingLink] = useState<BookingLinkResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [copied, setCopied] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  // When a crash or reload interrupted an edit, the unsaved text comes back from
  // the browser and this holds the time it was last typed (for the banner).
  const [restoredAt, setRestoredAt] = useState<number | null>(null);

  const draftKey = (id: number) => `clinic:profile-draft:${id}`;
  const formFromProfile = (u: User): ProfileUpdateRequest => ({
    first_name: u.first_name || '',
    last_name: u.last_name || '',
    specialty: u.specialty || '',
    license_number: u.license_number || '',
    phone: u.phone || '',
    profile_bio: u.profile_bio || '',
    specialties: u.specialties || [],
    video_intro_url: u.video_intro_url || '',
    website_url: u.website_url || '',
  });
  /** "www.site.com" becomes "https://www.site.com" so the API does not bounce the save. */
  const normalizeUrl = (raw?: string | null): string => {
    const v = (raw || '').trim();
    if (!v) return '';
    return v.includes('://') ? v : `https://${v.replace(/^\/+/, '')}`;
  };

  // Form state
  const [formData, setFormData] = useState<ProfileUpdateRequest>({
    first_name: '',
    last_name: '',
    specialty: '',
    license_number: '',
    phone: '',
    profile_bio: '',
    specialties: [],
    video_intro_url: '',
    website_url: '',
  });

  useEffect(() => {
    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      setError(null);
      const [profileData, linkData] = await Promise.all([
        memberProfileAPI.getProfile(),
        memberProfileAPI.getBookingLink().catch(() => null), // May fail if no slug
      ]);
      setProfile(profileData);
      setBookingLink(linkData);
      const serverForm = formFromProfile(profileData);
      const draft = loadDraft<ProfileUpdateRequest>(draftKey(profileData.id));
      if (draft && draftDiffers(draft.value, serverForm)) {
        setFormData({ ...serverForm, ...draft.value });
        setIsEditing(true);
        setRestoredAt(draft.savedAt);
      } else {
        if (draft) clearDraft(draftKey(profileData.id));
        setFormData(serverForm);
      }
    } catch (err: any) {
      setError(apiErrorMessage(err, 'Failed to load profile'));
      console.error('Error fetching profile:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isEditing && profile) saveDraft(draftKey(profile.id), formData);
  }, [formData, isEditing, profile]);

  const handleSave = async () => {
    try {
      setSaving(true);
      const payload: ProfileUpdateRequest = {
        ...formData,
        website_url: normalizeUrl(formData.website_url),
        video_intro_url: (formData.video_intro_url || '').trim(),
      };
      const updatedProfile = await memberProfileAPI.updateProfile(payload);
      setProfile(updatedProfile);
      setFormData(formFromProfile(updatedProfile));
      if (profile) clearDraft(draftKey(profile.id));
      setRestoredAt(null);
      setIsEditing(false);
      // Update auth context so sidebar/dashboard reflect changes immediately
      updateUser({
        first_name: updatedProfile.first_name,
        last_name: updatedProfile.last_name,
      });
      toast.success('Profile updated successfully');
    } catch (err: any) {
      // The draft stays in the browser until the save succeeds, so a rejected
      // field never costs the clinician the rest of what they typed.
      toast.error(apiErrorMessage(err, 'Failed to update profile'));
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (profile) {
      setFormData(formFromProfile(profile));
      clearDraft(draftKey(profile.id));
    }
    setRestoredAt(null);
    setIsEditing(false);
  };

  // Construct booking URL using current origin to ensure correct domain
  const getBookingUrl = () => {
    if (bookingLink?.slug) {
      return `${window.location.origin}/clinicians/${bookingLink.slug}`;
    }
    return null;
  };

  const handleCopyLink = async () => {
    const url = getBookingUrl();
    if (url) {
      try {
        await navigator.clipboard.writeText(url);
        setCopied(true);
        toast.success('Booking link copied to clipboard');
        setTimeout(() => setCopied(false), 2000);
      } catch {
        toast.error('Failed to copy link');
      }
    }
  };

  const handleRegenerateSlug = async () => {
    if (!window.confirm('Are you sure? This will invalidate your current booking link. Any existing shared links will stop working.')) {
      return;
    }
    try {
      setRegenerating(true);
      const result = await memberProfileAPI.regenerateSlug();
      const newLinkData = await memberProfileAPI.getBookingLink();
      setBookingLink(newLinkData);
      if (profile) {
        setProfile({ ...profile, booking_slug: result.new_slug });
      }
      toast.success('Booking link regenerated');
    } catch (err: any) {
      toast.error(apiErrorMessage(err, 'Failed to regenerate link'));
    } finally {
      setRegenerating(false);
    }
  };

  if (loading) {
    return (
      <div>
        <h1 className="text-3xl font-bold text-clinic-dark mb-8 font-body">Profile</h1>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-clinic-primary"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <h1 className="text-3xl font-bold text-clinic-dark mb-8 font-body">Profile</h1>
        <StatusBanner
          variant="error"
          title="Unable to load profile"
          message={error}
          actions={
            <button
              onClick={fetchProfile}
              className="text-sm text-clinic-accent hover:underline"
            >
              Try again
            </button>
          }
        />
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-start gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-clinic-dark font-body">Profile</h1>
          <p className="text-clinic-muted mt-2">
            This is what clients see. Booking access &amp; room preference live in{' '}
            <Link to="/member/settings" className="text-clinic-primary hover:underline">Settings</Link>.
          </p>
        </div>
        {!isEditing ? (
          <div className="flex space-x-3 flex-shrink-0">
            {getBookingUrl() && (
              <Button
                variant="ghost"
                as="a"
                href={getBookingUrl() as string}
                target="_blank"
                rel="noopener noreferrer"
              >
                Preview
              </Button>
            )}
            <Button
              variant="secondary"
              onClick={() => setIsEditing(true)}
              leftIcon={<PencilIcon className="h-4 w-4" />}
            >
              Edit Profile
            </Button>
          </div>
        ) : (
          <div className="flex space-x-3 flex-shrink-0">
            <Button variant="ghost" onClick={handleCancel}>
              Cancel
            </Button>
            <Button onClick={handleSave} isLoading={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        )}
      </div>

      {isEditing && restoredAt !== null && (
        <div role="status" className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-card border border-clinic-accent/40 bg-clinic-cream/60 px-4 py-3 text-sm text-clinic-dark">
          <span>
            We restored the edits you hadn’t saved (last typed {new Date(restoredAt).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}).
            Choose <strong>Save Changes</strong> to keep them, or <strong>Cancel</strong> to discard.
          </span>
          <button type="button" className="text-clinic-primary hover:underline" onClick={() => setRestoredAt(null)}>Got it</button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Profile Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Info Card */}
          <Card className="p-6">
            <CardHeader title="Basic Information" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-clinic-dark mb-1.5">First Name</label>
                {isEditing ? (
                  <Input
                    value={formData.first_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, first_name: e.target.value }))}
                  />
                ) : (
                  <p className="text-clinic-dark py-2">{profile?.first_name}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-clinic-dark mb-1.5">Last Name</label>
                {isEditing ? (
                  <Input
                    value={formData.last_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, last_name: e.target.value }))}
                  />
                ) : (
                  <p className="text-clinic-dark py-2">{profile?.last_name}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-clinic-dark mb-1.5">Email</label>
                <p className="text-clinic-dark py-2">{profile?.email}</p>
                <p className="text-xs text-clinic-muted mt-1">Contact admin to change email</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-clinic-dark mb-1.5">Phone</label>
                {isEditing ? (
                  <Input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="(555) 123-4567"
                  />
                ) : (
                  <p className="text-clinic-dark py-2">{profile?.phone || '-'}</p>
                )}
              </div>
            </div>
          </Card>

          {/* Professional Info Card */}
          <Card className="p-6">
            <CardHeader title="Professional Information" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-clinic-dark mb-1.5">Professional Title</label>
                {isEditing ? (
                  <Input
                    value={formData.specialty}
                    placeholder="e.g. Registered Psychologist"
                    onChange={(e) => setFormData(prev => ({ ...prev, specialty: e.target.value }))}
                  />
                ) : (
                  <p className="text-clinic-dark py-2">{profile?.specialty || '-'}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-clinic-dark mb-1.5">License Number</label>
                {isEditing ? (
                  <Input
                    value={formData.license_number}
                    onChange={(e) => setFormData(prev => ({ ...prev, license_number: e.target.value }))}
                  />
                ) : (
                  <p className="text-clinic-dark py-2">{profile?.license_number || '-'}</p>
                )}
              </div>
            </div>

            {/* Areas of Focus (3c tags) — searchable in the public directory */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-clinic-dark mb-1.5">Areas of Focus</label>
              {isEditing ? (
                <ChipInput
                  value={formData.specialties || []}
                  onChange={(next) => setFormData(prev => ({ ...prev, specialties: next }))}
                  suggestions={COMMON_SPECIALTIES}
                  placeholder="e.g. Psychologist, Anxiety, CBT…"
                />
              ) : (
                (profile?.specialties && profile.specialties.length > 0) ? (
                  <div className="flex flex-wrap gap-2 py-2">
                    {profile.specialties.map((tag) => (
                      <span key={tag} className="inline-flex items-center rounded-full bg-clinic-primary/10 text-clinic-primary-dark text-sm font-medium px-3 py-1">
                        {tag}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-clinic-muted py-2">No tags yet — add tags so clients can find you in search.</p>
                )
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-clinic-dark mb-1.5">
                Bio
                {isEditing && <span className="text-clinic-muted font-normal ml-2">({(formData.profile_bio || '').length}/2000)</span>}
              </label>
              {isEditing ? (
                <Textarea
                  value={formData.profile_bio}
                  onChange={(e) => setFormData(prev => ({ ...prev, profile_bio: e.target.value }))}
                  rows={4}
                  maxLength={2000}
                  placeholder="Tell clients about your background, approach, and areas of expertise..."
                />
              ) : (
                <p className="text-clinic-dark whitespace-pre-wrap py-2">{profile?.profile_bio || 'No bio provided'}</p>
              )}
            </div>

            {/* Intro video + website (3d) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-clinic-dark mb-1.5">Intro Video</label>
                {isEditing ? (
                  <Input
                    type="url"
                    placeholder="YouTube or Vimeo link"
                    value={formData.video_intro_url || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, video_intro_url: e.target.value }))}
                  />
                ) : (
                  profile?.video_intro_url ? (
                    <a href={profile.video_intro_url} target="_blank" rel="noopener noreferrer" className="text-clinic-primary hover:underline break-all py-2 inline-block">{profile.video_intro_url}</a>
                  ) : (
                    <p className="text-clinic-muted py-2">—</p>
                  )
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-clinic-dark mb-1.5">Website</label>
                {isEditing ? (
                  <Input
                    type="url"
                    placeholder="https://your-site.com"
                    value={formData.website_url || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, website_url: e.target.value }))}
                  />
                ) : (
                  profile?.website_url ? (
                    <a href={profile.website_url} target="_blank" rel="noopener noreferrer" className="text-clinic-primary hover:underline break-all py-2 inline-block">{profile.website_url}</a>
                  ) : (
                    <p className="text-clinic-muted py-2">—</p>
                  )
                )}
              </div>
            </div>
          </Card>

          {/* Services offered (3d) — saved immediately, independent of profile Save */}
          <Card className="p-6">
            <CardHeader title="Services Offered" />
            <ServicesEditor editing={isEditing} />
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Booking Link Card */}
          <Card className="p-6">
            <CardHeader title="Booking Link" />
            {bookingLink && getBookingUrl() ? (
              <>
                <div className="bg-clinic-cream rounded-lg p-3 mb-3">
                  <div className="flex items-center">
                    <LinkIcon className="h-4 w-4 text-clinic-muted mr-2 flex-shrink-0" />
                    <p className="text-sm text-clinic-dark break-all font-mono">{getBookingUrl()}</p>
                  </div>
                </div>
                <div className="flex space-x-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={handleCopyLink}
                    className="flex-1"
                    leftIcon={copied ? <CheckCircleIcon className="h-4 w-4 text-clinic-primary" /> : <ClipboardDocumentIcon className="h-4 w-4" />}
                  >
                    {copied ? 'Copied!' : 'Copy'}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleRegenerateSlug}
                    isLoading={regenerating}
                    title="Generate new link"
                  >
                    <ArrowPathIcon className={`h-4 w-4 ${regenerating ? 'animate-spin' : ''}`} />
                  </Button>
                </div>
                <p className="text-xs text-clinic-muted mt-3">
                  Share this link with patients so they can book appointments with you.
                </p>
              </>
            ) : (
              <p className="text-sm text-clinic-muted">No booking link available. Contact admin if this is unexpected.</p>
            )}
          </Card>

          {/* Photo Card */}
          <Card className="p-6">
            <CardHeader title="Profile Photo" />
            <ProfilePhotoUpload
              currentPhotoUrl={profile?.profile_photo_url}
              onPhotoChange={(newUrl) => {
                if (profile) {
                  setProfile({ ...profile, profile_photo_url: newUrl ?? undefined });
                }
                // Update AuthContext so Header updates without page reload
                updateUser({ profile_photo_url: newUrl ?? undefined });
              }}
            />
          </Card>

          {/* Account Status */}
          <Card className="p-6">
            <CardHeader title="Account Status" />
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-clinic-muted">Status</span>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  profile?.is_active
                    ? 'bg-clinic-primary/20 text-clinic-primary'
                    : 'bg-clinic-accent/20 text-clinic-accent'
                }`}>
                  {profile?.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-clinic-muted">Approval</span>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  profile?.approval_status === 'approved'
                    ? 'bg-clinic-primary/20 text-clinic-primary'
                    : profile?.approval_status === 'pending'
                      ? 'bg-clinic-gold/20 text-clinic-gold'
                      : 'bg-clinic-accent/20 text-clinic-accent'
                }`}>
                  {profile?.approval_status ? profile.approval_status.charAt(0).toUpperCase() + profile.approval_status.slice(1) : '-'}
                </span>
              </div>
              <div className="flex justify-between items-center pt-2 border-t border-clinic-primary-lighter">
                <span className="text-sm text-clinic-muted">Member since</span>
                <span className="text-sm text-clinic-dark">
                  {profile?.created_at ? formatShortDate(profile.created_at) : '-'}
                </span>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default MemberDashboard;
